import styled, { css } from 'styled-components';
import { DEVICES } from '_constants';
import { UiColors } from 'assets/styles/old/global';

const POPUP_POSITIONS = {
  0: '25%',
  1: '45%',
  2: '70%',
};

export const ButtonSideBar = styled.button.attrs(() => ({
  ariaRequired: true,
  role: 'button',
}))`
  font-size: 14px;
  text-align: center;
  color: ${UiColors.peachColor};
  border: none;
  padding: 10px;
  border-radius: 2px;
  width: 150px;
  background-color: ${UiColors.forestColor};
  cursor: pointer;
  &:focus {
    border: 3px solid ${UiColors.focusColor};
    outline-color: ${UiColors.focusColor};
  }

  /* New design: 2px reads as square next to the redesigned controls, so the radius and weight come
     from the same tokens ui-kit/Button uses. The focus ring replaces the 3px border, which shifted
     the label when the button took focus. Colours are already the redesign's forest/peach.
     html[...] rather than :root[...]: stylis auto-prefixes a selector that opens with a
     pseudo-class, so ':root[data-new-ui] &' compiles to '.generatedClass:root[...]' and can never
     match. Nothing changes with the flag off. */
  html[data-new-ui='true'] & {
    border-radius: var(--btn-radius);
    font-weight: var(--btn-weight);

    &:hover {
      filter: brightness(1.15);
    }

    &:focus {
      border: none;
    }

    &:focus-visible {
      outline: 2px solid var(--dodger-blue);
      outline-offset: 2px;
    }
  }
`;

export const ButtonCell = styled.div`
  display: flex;
  /* Start at the left edge of the cell rather than centring the cluster. */
  justify-content: flex-start;

  /* New design: the three 34px buttons have to fit the 125px actions column, so spacing comes from
     one gap here instead of a 10px margin on every icon. The cell itself carries no left padding
     (see newUiHeaderRow in StoreMergerTable/styles.js -- it was zeroed to win back the width), so
     the 5px that keeps the first icon off the cell edge lives here. 5 + 3x34 + 2x4 = 115px, inside
     the 119px the column leaves after its 6px right padding. */
  ${({ $newUi }) =>
    $newUi &&
    css`
      gap: 4px;
      padding-left: 5px;

      ${ImageWrapper} {
        margin-right: 0;
      }
    `}
`;

export const Title = styled.div`
  font-size: 14px;
  line-height: 16px;
  margin: 0 0 12px;
`;

export const SubTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #737373;
  margin: 0 0 15px;
`;

export const ListItem = styled.li`
  &:not(:first-child) {
    margin: 30px 0 0;
  }
`;

export const List = styled.ul`
  background: var(--white);
  flex-basis: 355px;
  max-width: 30%;
  border-top: 1px solid ${UiColors.strokeColor};
  border-right: 1px solid ${UiColors.strokeColor};
  border-bottom: 1px solid ${UiColors.strokeColor};
  padding: 18px;
  margin: 0;
  list-style: none;

  @media ${DEVICES.laptop} {
    max-width: 100%;
    border: 1px solid ${UiColors.strokeColor};
    margin: 15px 0 0;
    min-width: 100px;
    flex-basis: auto;
  }
`;

export const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-right: 10px;

  :hover {
    cursor: pointer;
  }
`;

export const MiniButtonSideBar = styled.button.attrs(() => ({
  ariaRequired: true,
  role: 'button',
}))`
  border: none;
`;

export const TooltipContainer = styled.div`
  display: none;
  position: absolute;
  z-index: 10000;
  background-color: #333;
  color: #fff;
  padding: 5px;
  border-radius: 4px;
  font-size: 14px;
  min-width: 600px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  top: 75%;
  left: ${({ buttonIndex }) => POPUP_POSITIONS[buttonIndex]}};
  transform: translateX(-100%);
`;

export const ToolTipSection = styled.span`
  display: inline-block;

  ${({ isSubDesc }) =>
    isSubDesc &&
    css`
      margin-top: 10px;
    `}
`;

export const TooltipContent = styled.div`
  white-space: normal;
`;

export const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  &:hover ${TooltipContainer} {
    display: flex;
    opacity: 1;
  }
`;

/* New design: the merge/history/split actions as forest line icons in a tappable button. */
export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--forest);
  cursor: pointer;
  transition: filter 0.15s ease;

  /* Hover deliberately does NOT paint a background: --cloud (#f7f2f0) read as a white block on the
     peach-highlighted merger rows. Saturating the glyph instead is the hover the rest of this page
     uses (see AdvancedCaretButton, and the DARK button in ui-kit). */
  &:hover {
    filter: saturate(3);
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;
